import { LandingPageConfig } from "../types/landingPage";
import { ExtendedLandingPage } from "./ExtendedLandingPage";
import { VerificationPageContent } from "../pages/VerificationPage";
import { ContactPageContent } from "../pages/ContactPage";
import { ContactInformationPage } from "../pages/ContactInformationPage";
import { PersonalInformationPage } from "../pages/PersonalInformationPage";
import { RedirectPageContent } from "../pages/RedirectPage";
import { NoSitesAvailablePageContent } from "../pages/NoSitesAvailablePage";
import { QualifiedRedirectPageContent } from "../pages/QualifiedRedirectPage";
import { DirectRedirectPageContent } from "../pages/DirectRedirectPage";
import { ThankYouPageContent } from "../pages/ThankYouPage";
import { EmrConsentPageContent } from "../pages/EmrConsentPage";
import { useFeatureFlag } from "../hooks/useFeatureFlags";
import { useEffect } from "react";
import { useAnalytics } from "../hooks/useAnalytics.ts";

// Define the supported page types
export type LandingPageType =
  | "standard" // Initial landing page with questionnaire
  | "contact" // Contact form step
  | "personal" // Personal information step
  | "verification" // Email/SMS verification step
  | "redirect" // Redirect loading page to MATS portal
  | "no-sites-available" // No trial sites available page
  | "qualified-redirect" // Immediate redirect after qualification (skips contact form)
  | "direct-redirect" // Direct redirect to external study site (no pre-screening)
  | "thank-you" // Thank you page with no redirect
  | "emr-consent"; // A/B test: deferred EMR address + consent step (after initial form submit)

/**
 * User journey flow:
 * 1. standard (questionnaire) -> User answers pre-screening questions
 * 2. contact -> User completes contact form with personal details
 * 3. verification -> User verifies email/phone if needed (IF A ACCOUNT DOESN'T EXIST WE SKIP THIS PAGE)
 * 4. redirect -> Intermediate loading page that redirects to MATS portal
 * 5. no-sites-available -> Informs user no trial sites are available and redirects to HealthMatch platform
 * 6. thank-you -> Final thank you page after submission, without redirect. Only shown if configured.
 *
 * Alternative flow (when skipContactFormOnQualified is enabled):
 * 1. standard (questionnaire) -> User answers pre-screening questions
 * 2. qualified-redirect -> Shows qualification message and redirects to external URL (skips contact form)
 *
 * Direct redirect flow (when directRedirect is enabled):
 * 1. standard -> Immediately shows countdown page and redirects to external study site
 */

interface LandingPageFactoryProps {
  pageConfig: LandingPageConfig;
  pageType?: LandingPageType;
}

/**
 * Factory component for rendering different landing page types
 * with consistent configuration and styling
 */
export function LandingPageFactory({
  pageConfig: config,
  pageType = "standard",
}: LandingPageFactoryProps) {
  const { page } = useAnalytics();
  useEffect(() => {
    page();
  }, [page]);

  const useContactPageVariantA = useFeatureFlag(
    "useContactPageVariantA",
    false,
  );

  // Render the appropriate page based on the pageType
  switch (pageType) {
    case "contact":
      return useContactPageVariantA ? (
        <ContactInformationPage config={config} />
      ) : (
        <ContactPageContent config={config} />
      );
    case "personal":
      return <PersonalInformationPage config={config} />;
    case "verification": // This page is only required if the account already exists!!!
      return <VerificationPageContent config={config} />;
    case "redirect":
      return <RedirectPageContent config={config} />;
    case "no-sites-available":
      return <NoSitesAvailablePageContent config={config} />;
    case "qualified-redirect":
      return <QualifiedRedirectPageContent config={config} />;
    case "direct-redirect":
      return <DirectRedirectPageContent config={config} />;
    case "thank-you":
      return <ThankYouPageContent config={config} />;
    case "emr-consent":
      return <EmrConsentPageContent config={config} />;
    case "standard":
    default:
      // Direct redirect pages skip the questionnaire/CTA and go straight to countdown
      if (config.directRedirect) {
        return <DirectRedirectPageContent config={config} />;
      }
      return <ExtendedLandingPage config={config} />;
  }
}
